import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { normalizeCipherState, useGameStore } from '../../store/gameStore';
import { gameAPI } from '../../services/gameAPI';
import {
  appendRevealDiff,
  applyActionResult,
  buildSessionRefreshCompletionUpdate,
  nestActionEntityState,
} from '../../utils/actionResult';
import {
  Search,
  FileText,
  Users,
  Clock,
  Check,
  AlertCircle,
  Loader2,
  CheckCircle,
  AlertTriangle,
  ChevronRight,
  MapPin,
  Lightbulb,
  KeyRound,
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { MAX_EXAMINE_ROUNDS, getExamineFocusIds } from '../../utils/actionTargets';
import { parseActionError } from '../../utils/actionError';
import {
  NO_EVIDENCE_AT_LOCATION,
  TONE_CLASSES,
  examineCoverage,
  formatExamineCoverage,
  hasNonClueEvidenceAtLocation,
} from '../../utils/effectiveness';

// Base focus options (4 for easy, all 5 for medium/hard)
const BASE_FOCUS_OPTIONS = [
  {
    id: 'physical_traces',
    name: 'Physical Traces',
    icon: Search,
    description: 'Objects, weapons, fingerprints, forensic evidence.',
    detail: 'Search for physical items and forensic clues left behind.',
    color: '#4CAF50'
  },
  {
    id: 'paper_trail',
    name: 'Paper Trail',
    icon: FileText,
    description: 'Documents, letters, financial records, contracts.',
    detail: 'Look for written evidence and financial transactions.',
    color: '#2196F3'
  },
  {
    id: 'witness_accounts',
    name: 'Witness Accounts',
    icon: Users,
    description: 'Testimonies, overheard conversations, eyewitness reports.',
    detail: 'Gather information from people who saw or heard something.',
    color: '#9C27B0'
  },
  {
    id: 'timeline_patterns',
    name: 'Timeline & Patterns',
    icon: Clock,
    description: 'Timing inconsistencies, behavioral patterns, alibis.',
    detail: 'Analyze the sequence of events and look for discrepancies.',
    color: '#FF9800'
  }
];

// 5th focus option - only available on medium/hard
const ENVIRONMENTAL_FOCUS = {
  id: 'environmental_context',
  name: 'Environmental Context',
  icon: MapPin,
  description: 'Scene layout, exits, hiding spots, environmental clues.',
  detail: 'Study the physical environment and what it reveals about events.',
  color: '#607D8B'
};

const ALL_FOCUS_OPTIONS = [...BASE_FOCUS_OPTIONS, ENVIRONMENTAL_FOCUS];

// Focus options offered for a difficulty. The ids come from the shared rules
// module so the ActionBar's "this location is used up" filter and this modal
// can never disagree about which focuses exist.
const focusOptionsFor = (difficulty) => {
  const allowed = new Set(getExamineFocusIds(difficulty));
  return ALL_FOCUS_OPTIONS.filter((option) => allowed.has(option.id));
};

// Difficulty configuration for examine mini-game
const EXAMINE_CONFIG = {
  easy: {
    maxFocusesPerRound: 2,  // Select up to 2 focuses
    focusOptions: focusOptionsFor('easy'),  // 4 options
    maxRounds: MAX_EXAMINE_ROUNDS
  },
  medium: {
    maxFocusesPerRound: 2,  // Select up to 2 focuses
    focusOptions: focusOptionsFor('medium'),  // 5 options - must leave one out
    maxRounds: MAX_EXAMINE_ROUNDS
  },
  hard: {
    maxFocusesPerRound: 1,  // Only 1 focus per round
    focusOptions: focusOptionsFor('hard'),  // 5 options
    maxRounds: MAX_EXAMINE_ROUNDS
  }
};

export const ExamineModal = ({
  open,
  onClose,
  location,
  onComplete,
  usedFocuses = [], // Focuses already used at this location (from previous sessions)
  previousExaminations = 0, // Number of examination rounds already completed at this location
  // Tutorial mode callbacks
  onFocusSelected = null,
  onExaminationStarted = null,
  onContinueSearching = null,
  // Tutorial mode data (pre-scripted response)
  tutorialScriptData = null
}) => {
  const [selectedFocuses, setSelectedFocuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [allResults, setAllResults] = useState([]); // Track all examinations in this session
  const [sessionUsedFocuses, setSessionUsedFocuses] = useState([]); // Focuses used in this modal session
  const [error, setError] = useState(null);
  const [errorDetails, setErrorDetails] = useState(null);
  const [refreshError, setRefreshError] = useState(null);
  const [pendingRevealDiff, setPendingRevealDiff] = useState(null);
  const [contextHint, setContextHint] = useState(null); // Pre-selection context hint
  const [lastSelectedFocus, setLastSelectedFocus] = useState(null); // Track last selected focus for tutorial callback

  // Ref to track when transitioning from results to selection (prevents accidental close)
  const isContinuingRef = useRef(false);

  const sessionId = useGameStore((state) => state.sessionId);
  const selectedTheme = useGameStore((state) => state.selectedTheme);
  const selectedDifficulty = useGameStore((state) => state.selectedDifficulty);
  const clues = useGameStore((state) => state.clues);
  const gameState = useGameStore((state) => state);
  const updateGameState = useGameStore((state) => state.updateGameState);
  const addMessage = useGameStore((state) => state.addMessage);
  // Cipher system
  const unlockCipher = useGameStore((state) => state.unlockCipher);
  const addCipherHint = useGameStore((state) => state.addCipherHint);
  const setShowCipherModal = useGameStore((state) => state.setShowCipherModal);

  // Get difficulty-specific config (default to easy for safety)
  const examineConfig = EXAMINE_CONFIG[selectedDifficulty] || EXAMINE_CONFIG.easy;
  const { maxFocusesPerRound, focusOptions } = examineConfig;

  // Theme accents
  const themeAccent = {
    noir: '#DC143C',
    '90s_nyc': '#FF6B35',
    miami_vice: '#FF1493'
  }[selectedTheme] || '#DC143C';

  // Reset state when modal opens with new location
  useEffect(() => {
    if (open) {
      setSelectedFocuses([]);
      setResult(null);
      setAllResults([]);
      setSessionUsedFocuses([]);
      setError(null);
      setErrorDetails(null);
      setRefreshError(null);
      setPendingRevealDiff(null);
      setContextHint(null);
      setLastSelectedFocus(null);
      isContinuingRef.current = false;
    }
  }, [open, location?.id]);

  // Tutorial callback - emit event when focus is selected
  useEffect(() => {
    if (lastSelectedFocus && onFocusSelected) {
      onFocusSelected(lastSelectedFocus);
      setLastSelectedFocus(null);
    }
  }, [lastSelectedFocus, onFocusSelected]);

  // Detect if we're in tutorial mode (callbacks are only provided in tutorials)
  const isTutorialMode = onFocusSelected !== null || onExaminationStarted !== null;

  const refreshSessionAfterReveal = async (revealDiff) => {
    try {
      const savedData = await gameAPI.loadGameSession(sessionId);
      useGameStore.getState().loadSavedGame({
        theme: savedData.theme || gameState.selectedTheme || 'noir',
        difficulty: savedData.difficulty || gameState.selectedDifficulty || 'medium',
        caseId: savedData.caseId,
        sessionId: savedData.sessionId,
        gameState: savedData.gameState,
        messageHistory: savedData.messageHistory,
        guideCharacter: savedData.guideCharacter,
        caseData: savedData.caseData,
        lastImage: savedData.lastImage,
        lastImageVantage2: savedData.lastImageVantage2,
      });
      const refreshedState = useGameStore.getState();
      useGameStore.getState().updateGameState(buildSessionRefreshCompletionUpdate({
        didRefresh: true,
        currentNewlyDiscovered: refreshedState.newlyDiscovered,
        revealDiff,
      }));
      setRefreshError(null);
      setPendingRevealDiff(null);
      return true;
    } catch (refreshErr) {
      console.error('Session refresh after examination failed:', refreshErr);
      useGameStore.getState().updateGameState(buildSessionRefreshCompletionUpdate({
        didRefresh: false,
      }));
      setRefreshError('Case file refresh failed. Reload the case to show newly found evidence.');
      setPendingRevealDiff(revealDiff);
      return false;
    }
  };

  const handleReloadCase = async () => {
    const refreshed = await refreshSessionAfterReveal(null);
    if (!refreshed) {
      if (typeof window !== 'undefined' && window.location?.reload) window.location.reload();
      return;
    }
    setSelectedFocuses([]);
    setSessionUsedFocuses([]);
    setAllResults([]);
    setResult(null);
    setError(null);
    setErrorDetails(null);
  };

  // Fetch context hint when modal opens (with abort controller to prevent race conditions)
  useEffect(() => {
    if (!open || !location?.id || !sessionId) return;

    // Skip API call in tutorial mode
    if (isTutorialMode) return;

    const abortController = new AbortController();

    gameAPI.getExamineContext(sessionId, location.id, abortController.signal)
      .then(data => {
        // Only update state if this request wasn't aborted
        if (!abortController.signal.aborted) {
          setContextHint(data.contextHint);
        }
      })
      .catch(err => {
        // Ignore abort errors, log others
        if (err.name !== 'AbortError' && err.name !== 'CanceledError') {
          console.error('Failed to fetch examine context:', err);
        }
        // Don't show error - context hint is optional
      });

    // Cleanup: abort the request if modal closes or location changes
    return () => {
      abortController.abort();
    };
  }, [open, location?.id, sessionId, isTutorialMode]);

  // Combined used focuses (from DB + this session)
  const allUsedFocuses = [...new Set([...usedFocuses, ...sessionUsedFocuses])];
  // Use difficulty-specific focus options
  const availableFocuses = focusOptions.filter(f => !allUsedFocuses.includes(f.id));
  const allFocusesUsed = availableFocuses.length === 0;

  // Rounds used at this location. Both sources already include the round just
  // completed: handleExamine writes the server's rounds list to the store
  // (feeding `previousExaminations`) before `setResult`, and the latest result
  // keeps that same list. Adding the local round on top double-counts it (PG-37).
  const latestServerRounds = (result ?? allResults[allResults.length - 1])?.examinationRounds?.length ?? 0;
  const totalRoundsUsed = Math.max(previousExaminations, latestServerRounds);
  const maxRoundsReached = totalRoundsUsed >= MAX_EXAMINE_ROUNDS;

  const toggleFocus = (focusId) => {
    setSelectedFocuses(prev => {
      const isCurrentlySelected = prev.includes(focusId);
      const canAddMore = prev.length < maxFocusesPerRound;

      if (isCurrentlySelected) {
        // Deselecting - remove from array
        return prev.filter(f => f !== focusId);
      } else if (canAddMore) {
        // Selecting - add to array and trigger tutorial callback via state
        const newSelection = [...prev, focusId];
        setLastSelectedFocus(focusId);
        return newSelection;
      }
      return prev;
    });
  };

  const handleExamine = async () => {
    if (selectedFocuses.length === 0) return;

    // CRITICAL: Validate that we're still at the expected location
    // This prevents race conditions where user's location changed since modal opened
    if (location.id !== gameState.currentLocation) {
      console.error(`[ExamineModal] Location mismatch: modal has ${location.id}, but currentLocation is ${gameState.currentLocation}`);
      setError(`You're no longer at ${location.name}. Please close this and try again.`);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let response;

      // In tutorial mode, use pre-scripted data instead of API call
      if (isTutorialMode && tutorialScriptData) {
        // Simulate processing time for realism
        await new Promise(resolve => setTimeout(resolve, 500));

        // Use pre-scripted narrative and reveals from tutorial data
        // Map tutorial reveals format (clues/locations/documents) to API format (new_clues/new_locations/new_documents)
        const reveals = tutorialScriptData.reveals || {};
        response = {
          success: true,
          narrative: tutorialScriptData.narrative || 'You examine the area carefully...',
          revealedInfo: {
            new_clues: reveals.clues || [],
            new_locations: reveals.locations || [],
            new_documents: reveals.documents || [],
            new_suspects: reveals.suspects || [],
          },
          updatedState: {
            suspects: gameState.suspects,
            clues: gameState.clues,
            locations: gameState.locations,
            documents: gameState.documents,
            discovered: {
              ...gameState.discovered,
              suspects: [...new Set([...(gameState.discovered?.suspects || []), ...(tutorialScriptData.reveals?.suspects || [])])],
              clues: [...new Set([...(gameState.discovered?.clues || []), ...(tutorialScriptData.reveals?.clues || [])])],
              locations: [...new Set([...(gameState.discovered?.locations || []), ...(tutorialScriptData.reveals?.locations || [])])],
              documents: [...new Set([...(gameState.discovered?.documents || []), ...(tutorialScriptData.reveals?.documents || [])])]
            },
            turnCount: (gameState.turnCount || 0) + 1
          }
        };
      } else {
        // Normal game mode - call the examine API with selected focuses
        // Use gameState.currentLocation for safety (should match location.id after validation above)
        response = await gameAPI.processAction(
          sessionId,
          'examine',
          gameState.currentLocation,
          selectedFocuses
        );
      }

      const actionResult = applyActionResult(gameState, response);
      const revealDiff = actionResult.revealDiff;
      const entityState = nestActionEntityState(actionResult.nextState);

      // Store message in history with computed diff (not raw backend revealedInfo)
      // so Timeline/CaseFile only see truly discovered items
      if (response.narrative) {
        addMessage('examine', location.id, response.narrative, {
          new_suspects: revealDiff.suspects,
          new_clues: revealDiff.clues,
          new_locations: revealDiff.locations,
          new_documents: revealDiff.documents,
        });
      }
      const updatedCipherState = response.updatedState?.cipherState ?? response.updatedState?.cipher_state;
      const normalizedUpdatedCipherState = updatedCipherState !== undefined
        ? normalizeCipherState(updatedCipherState, gameState.cipherState)
        : null;
      const queuedCipherHints = Array.isArray(response.revealedInfo?.cipher_hints_queued)
        ? response.revealedInfo.cipher_hints_queued
        : [];
      const queuedCipherHintIds = new Set(queuedCipherHints);
      const cipherHintsFound = (response.revealedInfo?.cipher_hints_found || [])
        .filter(id => !queuedCipherHintIds.has(id));

      updateGameState({
        narrativeText: response.narrative,
        discovered: entityState.discovered,
        suspects: entityState.suspects,
        clues: entityState.clues,
        locations: entityState.locations,
        documents: entityState.documents,
        newlyDiscovered: actionResult.needsSessionRefresh
          ? gameState.newlyDiscovered
          : appendRevealDiff(gameState.newlyDiscovered, revealDiff),
        needsSessionRefresh: actionResult.needsSessionRefresh,
        hintedClues: response.updatedState?.hintedClues || gameState.hintedClues,
        newlyHinted: [
          ...(gameState.newlyHinted || []),
          ...(response.revealedInfo?.new_hinted_clues || []).filter(
            id => !(gameState.hintedClues || []).includes(id)
          )
        ],
        examinedFocuses: response.updatedState?.examinedFocuses || gameState.examinedFocuses,
        examinations: response.updatedState?.examinations || gameState.examinations,
        observationHints: response.observation_hints || [],
        currentLocation: response.updatedState?.currentLocation || gameState.currentLocation,
        turnCount: response.updatedState?.turnCount || gameState.turnCount,
        currentImage: response.newImage || gameState.currentImage,
        currentImageVantage2: response.newImageVantage2 || gameState.currentImageVantage2,
        ...(normalizedUpdatedCipherState && { cipherState: normalizedUpdatedCipherState }),
        ...(response.updatedState?.cipherPuzzle !== undefined && { cipherPuzzle: response.updatedState.cipherPuzzle }),
      });

      if (actionResult.needsSessionRefresh) {
        await refreshSessionAfterReveal(revealDiff);
      }

      // Handle cipher unlock
      const cipherUnlocked = response.revealedInfo?.cipher_unlocked || normalizedUpdatedCipherState?.unlocked;
      if (cipherUnlocked && !gameState.cipherState?.unlocked) {
        unlockCipher();
        // Update cipher puzzle data if provided
        if (response.updatedState?.cipherPuzzle) {
          updateGameState({ cipherPuzzle: response.updatedState.cipherPuzzle });
        }
        toast({
          title: 'Cipher Discovered!',
          description: 'You found a coded message. Check the Cipher button to decode it.',
        });
      }

      if (queuedCipherHints.length > 0) {
        toast({ title: 'You found a cipher-related clue. It will matter once you find the coded message.' });
      }

      // Handle cipher hints found during examination
      cipherHintsFound.forEach(hintId => {
        addCipherHint(hintId);
      });

      // Build result object for display
      const examinationResult = {
        focuses: selectedFocuses,
        narrative: response.narrative,
        cluesFound: revealDiff.clues,
        locationsFound: revealDiff.locations,
        documentsFound: revealDiff.documents,
        observationHints: response.observation_hints || [],
        effectiveness: response.updatedState?.examinations?.[location.id]?.slice(-1)[0]?.effectiveness || 0,
        // Every examination round at this location, from the response rather
        // than the store, so the round just completed is always included. The
        // result view reports cumulative coverage off these instead of the
        // backend's per-round ratio.
        examinationRounds: response.updatedState?.examinations?.[location.id] || [],
        totalCluesAtLocation: response.updatedState?.examinations?.[location.id]?.slice(-1)[0]?.total_clues_at_location,
        turnCount: response.updatedState?.turnCount || 0,
        cipherUnlocked: cipherUnlocked && !gameState.cipherState?.unlocked,
        cipherHintsFound: cipherHintsFound,
        hintedCluesFound: response.revealedInfo?.new_hinted_clues || [],
      };

      setResult(examinationResult);

      // Mark these focuses as used in this session
      setSessionUsedFocuses(prev => [...new Set([...prev, ...selectedFocuses])]);

      // Emit tutorial event after successful examination
      if (onExaminationStarted) {
        onExaminationStarted(selectedFocuses);
      }

    } catch (err) {
      console.error('Examination failed:', err);
      const parsed = parseActionError(err);
      setError(err.response?.data?.detail ? parsed.publicMessage : 'Examination failed. Please try again.');
      setErrorDetails(parsed);
    } finally {
      setLoading(false);
      setSelectedFocuses([]);
    }
  };

  const handleContinue = () => {
    // Mark as continuing to prevent accidental close during state transition
    isContinuingRef.current = true;

    // Store current result
    if (result) {
      setAllResults(prev => [...prev, result]);
    }
    // Go back to selection view
    setResult(null);

    // Emit tutorial event when user continues to next round
    if (onContinueSearching) {
      onContinueSearching();
    }
  };

  // Clear isContinuingRef after React completes the state transition
  // This is triggered when result becomes null after handleContinue sets it
  useEffect(() => {
    if (result === null && isContinuingRef.current) {
      isContinuingRef.current = false;
    }
  }, [result]);

  const handleClose = () => {
    // Collect all results including current one
    const resultsToReturn = result ? [...allResults, result] : allResults;

    // Call onComplete with combined results
    if (resultsToReturn.length > 0) {
      const combinedResult = {
        allResults: resultsToReturn,
        narrative: resultsToReturn.map(r => r.narrative).join('\n\n---\n\n'),
        totalCluesFound: resultsToReturn.flatMap(r => r.cluesFound),
        focusesUsed: resultsToReturn.flatMap(r => r.focuses)
      };
      onComplete(combinedResult);
    }

    onClose();
    // Reset state
    setResult(null);
    setAllResults([]);
    setSelectedFocuses([]);
    setSessionUsedFocuses([]);
    setError(null);
  };

  if (!location) return null;

  // Calculate if more focuses are available and rounds permit
  const remainingFocuses = focusOptions.filter(
    f => !allUsedFocuses.includes(f.id) && !selectedFocuses.includes(f.id)
  );

  // Can continue only if: have a result, focuses available, AND haven't hit max rounds
  // In tutorial mode, disable multi-round examination to simplify the experience
  const canContinue = result && remainingFocuses.length > 0 && !maxRoundsReached && !isTutorialMode;

  // Smarter close handler - prevents accidental closes during results/loading
  // User must click explicit buttons to close when viewing results
  const handleDialogOpenChange = (isOpen) => {
    if (!isOpen) {
      // Tutorial mode: never close via backdrop/Esc/X. The tutorial drives the modal
      // through explicit button events (focus_selected, examination_started,
      // modal_examine_closed via the Complete button). Backdrop pointerdown bypasses
      // TutorialOverlay's click-only blocker, so without this guard a missed tap on
      // a focus button could close the modal mid-tutorial and leave the user stuck.
      if (isTutorialMode) {
        return;
      }
      // Only allow closing via backdrop/escape in selection view when:
      // - Not loading
      // - Not viewing results
      // - Not transitioning from results to selection (continuing)
      if (!result && !loading && !isContinuingRef.current) {
        handleClose();
      }
      // When showing results, loading, or continuing, require explicit button click
      // (the buttons call handleClose directly)
    }
  };

  // RESULT VIEW
  if (result) {
    // Cumulative coverage of this location, not the backend's per-round ratio:
    // the numerator is every clue on this location's examine-findable roster
    // that the player HOLDS right now, by whatever route — the backend derives
    // it per payload as `clues_known_at_location`
    // (`backend/helpers/examination_coverage.py`) and `examineCoverage` takes
    // the max of that and the ids found by examine here. A player who holds
    // everything findable reads 100% even if a cipher reward or an
    // interrogation handed one of them over. The rounds come
    // from the action response (see `examinationRounds` above), which already
    // contains the round just completed, so nothing else needs adding — and
    // adding `revealDiff.clues` would not be safe: it is server-discovered
    // minus the client's previous discovered, scoped to neither this location
    // nor this examine, so a desync could re-create a false "Location cleared".
    const coverage = examineCoverage({
      rounds: result.examinationRounds,
      total: result.totalCluesAtLocation,
    });
    // Distinguish "nothing to find here" from "no reading available": the
    // backend states the former with an explicit zero.
    //
    // I7 / M-1: `total_clues_at_location` counts CLUES only, so a location whose
    // evidence is a document (noir `doc_2` at `institutional`) has M=0. Saying
    // "no evidence" there contradicts the result on screen or the Case File, so
    // suppress the line when a round here recorded a document OR the player
    // holds a document from here by any route (e.g. an interview), and render
    // no counter at all — there is no documents denominator to show. The store
    // was updated from this response before the result view rendered, so
    // `gameState` already carries the post-examine documents.
    const documentEvidenceHere = hasNonClueEvidenceAtLocation({
      locationId: location.id,
      rounds: result.examinationRounds,
      documents: gameState.documents,
      discoveredDocumentIds: gameState.discovered?.documents,
    });
    const hasNoEvidence = Number(result.totalCluesAtLocation) === 0 && !documentEvidenceHere;

    return (
      <Dialog open={open} onOpenChange={handleDialogOpenChange}>
        <DialogContent
          className="w-full h-[100dvh] sm:max-w-3xl sm:h-auto sm:max-h-[90vh] overflow-y-auto bg-slate-900 border-2"
          style={{ borderColor: themeAccent }}
          hideCloseButton={isTutorialMode}
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-lg sm:text-xl font-bold text-white flex items-center gap-2 pr-10">
              <MapPin className="w-5 h-5" style={{ color: themeAccent }} />
              Examination Results - {location.name}
            </DialogTitle>
          </DialogHeader>

          {/* Effectiveness feedback - hide in tutorial mode since it's not relevant */}
          {!isTutorialMode && (
            <div className="mb-4 p-3 rounded-lg bg-slate-800">
              {coverage ? (
                <div className="flex items-center gap-2 mb-2" data-testid="examine-coverage">
                  {coverage.tone === 'positive' ? (
                    <CheckCircle className={`w-5 h-5 ${TONE_CLASSES.positive.text}`} />
                  ) : coverage.tone === 'neutral' ? (
                    <AlertCircle className={`w-5 h-5 ${TONE_CLASSES.neutral.text}`} />
                  ) : (
                    <AlertTriangle className={`w-5 h-5 ${TONE_CLASSES[coverage.tone].text}`} />
                  )}
                  <span className={`text-sm font-medium ${TONE_CLASSES[coverage.tone].text}`}>
                    {coverage.percent === 100 ? 'Location cleared' :
                     coverage.found === 0 ? 'Nothing found here yet' : 'Partly searched'}
                  </span>
                  <span className="text-xs text-slate-400 ml-auto">
                    {formatExamineCoverage(coverage)}
                  </span>
                </div>
              ) : hasNoEvidence && (
                <div className="flex items-center gap-2 mb-2" data-testid="examine-no-evidence">
                  <AlertCircle className={`w-5 h-5 ${TONE_CLASSES.neutral.text}`} />
                  <span className={`text-sm font-medium ${TONE_CLASSES.neutral.text}`}>
                    {NO_EVIDENCE_AT_LOCATION}
                  </span>
                </div>
              )}
              <p className="text-sm text-slate-300">
                Focused on: {result.focuses.map(f =>
                  focusOptions.find(opt => opt.id === f)?.name || f
                ).join(' + ')}
              </p>
            </div>
          )}

          {/* Narrative */}
          <div className="bg-slate-800 rounded-lg p-4 mb-4">
            <div className="prose prose-invert prose-sm max-w-none">
              {result.narrative.split('\n').map((paragraph, index) => (
                <p key={index} className="text-slate-200 mb-3 last:mb-0">
                  {paragraph}
                </p>
              ))}
            </div>
          </div>

          {refreshError && (
            <div className="mb-4 p-3 rounded-lg bg-amber-900/30 border border-amber-500/40">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex items-start gap-2 flex-1">
                  <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-300 flex-shrink-0" />
                  <p className="text-sm text-amber-100">{refreshError}</p>
                </div>
                <Button
                  onClick={() => refreshSessionAfterReveal(pendingRevealDiff)}
                  variant="outline"
                  className="border-amber-400/40 bg-amber-900/40 text-amber-100 hover:bg-amber-800/60 hover:text-white"
                >
                  Reload case
                </Button>
              </div>
            </div>
          )}

          {/* Cipher Unlock Discovery */}
          {result.cipherUnlocked && (
            <div className="mb-4 p-3 rounded-lg bg-purple-900/30 border border-purple-500 animate-pulse">
              <h4 className="text-sm font-semibold text-purple-300 mb-2 flex items-center gap-2">
                <KeyRound className="w-4 h-4" style={{ color: themeAccent }} />
                Cipher Discovered!
              </h4>
              <p className="text-sm text-purple-200">
                You found a coded message! A new "Cipher" button has appeared in your action bar.
                Decode the message to uncover hidden information about the case.
              </p>
            </div>
          )}

          {/* Clues Found */}
          {result.cluesFound?.length > 0 && (
            <div className="mb-4 p-3 rounded-lg bg-green-900/30 border border-green-700">
              <h4 className="text-sm font-semibold text-green-400 mb-2 flex items-center gap-2">
                <Search className="w-4 h-4" />
                Evidence Discovered:
              </h4>
              <ul className="text-sm text-green-300 space-y-1">
                {result.cluesFound.map((clueId) => {
                  const clue = (clues || []).find(c => c.id === clueId);
                  if (!clue) {
                    console.warn(`[ExamineModal] Missing clue lookup: clueId="${clueId}" not found in clues store. Location: ${location?.name || 'unknown'}`);
                  }
                  return (
                    <li key={clueId} className="flex items-center gap-2">
                      <Check className="w-3 h-3" />
                      {clue?.name || 'New evidence'}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Hinted Clues — Easy difficulty only */}
          {selectedDifficulty === 'easy' && result.hintedCluesFound?.length > 0 && (
            <div className="mb-4 p-3 rounded-lg bg-amber-900/30 border border-amber-700">
              <h4 className="text-sm font-semibold text-amber-400 mb-2 flex items-center gap-2">
                <Lightbulb className="w-4 h-4" />
                New Leads — Visit these locations to find evidence:
              </h4>
              <ul className="text-sm text-amber-300 space-y-1">
                {result.hintedCluesFound.map((clueId) => {
                  const clue = (clues || []).find(c => c.id === clueId);
                  const loc = clue?.location
                    ? (gameState.locations || []).find(l => l.id === clue.location)
                    : null;
                  return (
                    <li key={clueId} className="flex items-center gap-2">
                      <Lightbulb className="w-3 h-3" />
                      <span>
                        {clue?.name || 'New lead'}
                        {loc && (
                          <span className="text-amber-500 text-xs ml-1">
                            — at {loc.name}
                          </span>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Observation Hints (missed clues) */}
          {result.observationHints?.length > 0 && (
            <div className="mb-4 p-3 rounded-lg bg-yellow-900/30 border border-yellow-700">
              <h4 className="text-sm font-semibold text-yellow-400 mb-2 flex items-center gap-2">
                <Lightbulb className="w-4 h-4" />
                You noticed something...
              </h4>
              <ul className="text-sm text-yellow-300 space-y-2">
                {result.observationHints.map((hint, idx) => (
                  <li key={idx}>
                    <p className="italic">{hint.hint}</p>
                    {hint.required_focus && (
                      <p className="text-xs text-yellow-500 mt-1">
                        Try focusing on: {hint.required_focus.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            {canContinue ? (
              <>
                <Button
                  data-tutorial="examine-continue-button"
                  onClick={handleContinue}
                  className="flex-1 min-h-touch font-semibold"
                  style={{ backgroundColor: themeAccent }}
                >
                  Continue Searching
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
                <Button
                  onClick={handleClose}
                  variant="outline"
                  className="flex-1 min-h-touch border-slate-600 text-slate-300 hover:bg-slate-800"
                >
                  Done Examining
                </Button>
              </>
            ) : (
              <Button
                data-tutorial="examine-complete-button"
                onClick={handleClose}
                className="w-full min-h-touch font-semibold"
                style={{ backgroundColor: themeAccent }}
              >
                Complete Examination
              </Button>
            )}
          </div>

          {!canContinue && (allFocusesUsed || maxRoundsReached) && (
            <p className="text-xs text-center text-slate-500 mt-2">
              {maxRoundsReached
                ? `Maximum examination rounds (${MAX_EXAMINE_ROUNDS}) reached at this location.`
                : 'All investigation focuses have been used at this location.'}
            </p>
          )}
        </DialogContent>
      </Dialog>
    );
  }

  // SELECTION VIEW
  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent
        className="w-full h-[100dvh] sm:max-w-2xl sm:h-auto sm:max-h-[90vh] overflow-y-auto bg-slate-900 border-2"
        style={{ borderColor: themeAccent }}
        hideCloseButton={isTutorialMode}
        onInteractOutside={(e) => loading && e.preventDefault()}
        onEscapeKeyDown={(e) => loading && e.preventDefault()}
      >
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="text-lg sm:text-xl font-bold text-white flex items-center gap-2 pr-10">
            <Search className="w-5 h-5" style={{ color: themeAccent }} />
            Examine {location.name}
          </DialogTitle>
        </DialogHeader>

        {error ? (
          <div className="p-4 bg-red-900/30 border border-red-700 rounded-lg">
            <p className="text-red-300">{error}</p>
            <Button
              onClick={errorDetails?.retryable === true && errorDetails?.retryScope === 'refetch_session'
                ? handleReloadCase
                : () => { setError(null); setErrorDetails(null); }}
              className="mt-3 border-slate-600 text-white hover:bg-slate-700"
              variant="outline"
            >
              {errorDetails?.retryable === true && errorDetails?.retryScope === 'refetch_session'
                ? 'Reload case' : 'Try Again'}
            </Button>
          </div>
        ) : loading ? (
          <div className="py-12 flex flex-col items-center gap-4">
            <Loader2 className="w-8 h-8 animate-spin" style={{ color: themeAccent }} />
            <p className="text-slate-400">Examining location...</p>
            <p className="text-xs text-slate-500">Searching for evidence</p>
          </div>
        ) : allFocusesUsed || maxRoundsReached ? (
          <div className="py-8 text-center">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-slate-500" />
            <p className="text-slate-300 mb-4">
              {maxRoundsReached
                ? `You've completed the maximum ${MAX_EXAMINE_ROUNDS} examination rounds at this location.`
                : "You've used all investigation focuses at this location."}
            </p>
            {(allResults.length > 0 || previousExaminations > 0) && (
              <p className="text-slate-500 text-sm mb-6">
                {allResults.length > 0
                  ? `Found ${allResults.flatMap(r => r.cluesFound).length} clue(s) during this examination.`
                  : 'Review your Case File to see what you found previously.'}
              </p>
            )}
            <Button
              onClick={handleClose}
              className="min-h-touch font-semibold"
              style={{ backgroundColor: themeAccent }}
            >
              {allResults.length > 0 ? 'Complete Examination' : 'Close'}
            </Button>
          </div>
        ) : (
          <>
            {/* Previously used focuses hint */}
            {allUsedFocuses.length > 0 && (
              <div className="mb-4 p-3 bg-slate-800 rounded-lg border-l-4" style={{ borderLeftColor: themeAccent }}>
                <p className="text-sm text-slate-300">
                  <span className="text-slate-400">Previously investigated:</span>{' '}
                  {allUsedFocuses.map(f =>
                    focusOptions.find(opt => opt.id === f)?.name || f
                  ).join(', ')}
                </p>
              </div>
            )}

            {/* Location context hint - like personality hints in interrogation */}
            {contextHint && (
              <div className="mb-4 p-3 bg-slate-800/50 rounded-lg border border-slate-700">
                <p className="text-sm text-slate-300 italic">
                  {contextHint}
                </p>
              </div>
            )}

            {/* Focus count indicator */}
            <div className="mb-4 text-center">
              <span className="px-3 py-1 bg-slate-800 rounded-full text-sm text-slate-300">
                {maxFocusesPerRound === 1
                  ? `Select 1 focus (${availableFocuses.length} remaining)`
                  : `Select up to ${maxFocusesPerRound} focuses (${availableFocuses.length} remaining)`
                }
              </span>
            </div>

            {/* Focus selection grid */}
            <div className="mb-4">
              <h4 className="text-sm font-medium text-slate-300 mb-3">
                Choose your investigation focus:
              </h4>
              <div className="grid grid-cols-2 gap-2 sm:gap-3">
                {focusOptions.map(focus => {
                  const isSelected = selectedFocuses.includes(focus.id);
                  const isUsed = allUsedFocuses.includes(focus.id);
                  const isDisabled = isUsed || (selectedFocuses.length >= maxFocusesPerRound && !isSelected);
                  const Icon = focus.icon;

                  return (
                    <button
                      key={focus.id}
                      data-focus-id={focus.id}
                      onClick={() => !isDisabled && toggleFocus(focus.id)}
                      disabled={isDisabled}
                      className={`min-h-touch p-3 sm:p-4 rounded-lg border-2 transition-all text-left relative ${
                        isSelected
                          ? 'bg-slate-700 border-opacity-100'
                          : isUsed
                            ? 'bg-slate-800/50 border-slate-700 opacity-50 cursor-not-allowed'
                            : isDisabled
                              ? 'bg-slate-800/30 border-slate-700 opacity-50 cursor-not-allowed'
                              : 'bg-slate-800 border-slate-700 hover:border-slate-500'
                      }`}
                      style={{
                        borderColor: isSelected ? focus.color : undefined
                      }}
                    >
                      {/* Used indicator */}
                      {isUsed && (
                        <span className="absolute -top-2 -right-2 bg-slate-600 text-slate-300 text-xs font-bold px-2 py-0.5 rounded-full">
                          DONE
                        </span>
                      )}

                      {/* Selected indicator */}
                      {isSelected && (
                        <div
                          className="absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: focus.color }}
                        >
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      )}

                      <div className="flex items-center gap-2 mb-1 sm:mb-2">
                        <Icon
                          className="w-5 h-5"
                          style={{ color: isUsed ? '#64748b' : focus.color }}
                        />
                        <span className={`font-semibold text-sm sm:text-base ${isUsed ? 'text-slate-500' : 'text-white'}`}>
                          {focus.name}
                        </span>
                      </div>
                      <p className="text-xs text-slate-400 hidden sm:block">
                        {focus.description}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Selected focus detail */}
            {selectedFocuses.length > 0 && (
              <div className="mb-4 p-3 bg-slate-800 rounded-lg">
                <p className="text-sm text-slate-300">
                  {selectedFocuses.length === 1
                    ? focusOptions.find(f => f.id === selectedFocuses[0])?.detail
                    : `Combining ${focusOptions.find(f => f.id === selectedFocuses[0])?.name} and ${focusOptions.find(f => f.id === selectedFocuses[1])?.name} for a thorough search.`
                  }
                </p>
              </div>
            )}

            {/* Begin button */}
            <Button
              data-tutorial="examine-begin-button"
              onClick={handleExamine}
              disabled={selectedFocuses.length === 0}
              className={`w-full min-h-touch font-semibold ${
                selectedFocuses.length === 0
                  ? 'bg-slate-700 text-slate-400 cursor-not-allowed'
                  : ''
              }`}
              style={{ backgroundColor: selectedFocuses.length > 0 ? themeAccent : undefined }}
            >
              {selectedFocuses.length === 0 ? (
                'Select a focus to examine'
              ) : (
                <>
                  <Search className="w-4 h-4 mr-2" />
                  Begin Examination ({selectedFocuses.length} focus{selectedFocuses.length !== 1 ? 'es' : ''})
                </>
              )}
            </Button>

            {selectedFocuses.length === 1 && (
              <p className="text-xs text-center text-slate-500 mt-2">
                You can select one more focus for a broader search.
              </p>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ExamineModal;
